// eslint-disable-next-line no-restricted-imports
import { gql, useMutation, useQuery } from "@apollo/client";
import { Link, makeStyles, Typography } from "@material-ui/core";
import { useFlashMessage } from "components/legacy/FlashMessage/FlashMessage";
import IntegrationCard from "components/legacy/IntegrationCard";
import { GreenhouseIntegration } from "components/legacy/Settings/Integrations/types";
import {
  DisconnectGreenhouseIntegrationForCustomer,
  GreenhouseCardData,
} from "generated/schemaTypes";
import greenhouseIconLg from "icons/greenhouse-icon-lg.svg";
import _ from "lodash";
import React, { useState } from "react";
import { ResourceCustomTheme } from "styles/config";
import { captureSentryError } from "utils/errors/sentryError";

import GreenhouseApiModal from "./GreenhouseApiModal";
import GreenhouseWebhooksModal from "./GreenhouseWebhooksModal";

const useStyles = makeStyles((_theme: ResourceCustomTheme) => ({
  root: {},
  linkButton: {
    cursor: "pointer",
  },
}));

enum GreenhouseModalStates {
  WEBHOOKS,
  API,
  NONE,
}

type GreenhouseIntegrationCardProps = GreenhouseIntegration;

const DISCONNECT_GREENHOUSE_INTEGRATION = gql`
  mutation DisconnectGreenhouseIntegrationForCustomer {
    disconnectGreenhouse {
      success
      message
      code
      customer {
        id
        isGreenhouseIntegrated
        greenhouseIntegration {
          id
          type
          connected
          apiKeySuffix
          maildropAddress
          subdomain
          requiredWebhooksConfigured
          onBehalfOfUserId
        }
      }
    }
  }
`;

const GREENHOUSE_CARD_DATA = gql`
  query GreenhouseCardData {
    currentUserPrisma {
      id
      currentOrganization {
        id
        customer {
          id
          isGreenhouseIntegrated
          greenhouseIntegration {
            id
            type
            connected
            apiKeySuffix
            maildropAddress
            subdomain
            requiredWebhooksConfigured
            onBehalfOfUserId
          }
        }
      }
    }
  }
`;

function GreenhouseIntegrationCard({
  connected,
}: GreenhouseIntegrationCardProps) {
  const classes = useStyles();
  const { loading, data } = useQuery<GreenhouseCardData>(GREENHOUSE_CARD_DATA);

  const [openGreenhouseModal, setOpenGreenhouseModal] =
    useState<GreenhouseModalStates>(GreenhouseModalStates.NONE);
  const { setContent } = useFlashMessage();

  const [disconnect, { loading: disconnectSaving }] =
    useMutation<DisconnectGreenhouseIntegrationForCustomer>(
      DISCONNECT_GREENHOUSE_INTEGRATION
    );

  const onDisconnect = async () => {
    try {
      await disconnect();
    } catch (error) {
      setContent({
        content:
          "Something went wrong disconnecting your Greenhouse integration",
        severity: "error",
      });
      captureSentryError(error, "GreenhouseIntegrationCard onDisconnect");
    }
  };

  const onConnect = () => {
    setOpenGreenhouseModal(GreenhouseModalStates.API);
  };

  const customer = data?.currentUserPrisma?.currentOrganization?.customer;
  let configurationIssue: boolean | React.ReactNode = false;
  if (connected && !loading) {
    const issues: string[] = [];
    if (!customer?.greenhouseIntegration.requiredWebhooksConfigured) {
      issues.push("A Required Webhook is not configured");
    }
    if (!customer?.greenhouseIntegration?.onBehalfOfUserId) {
      issues.push("Missing On Behalf Of user");
    }
    if (issues.length) {
      configurationIssue = (
        <>
          {_.map(issues, (issue, index) => (
            <React.Fragment key={index}>
              {index !== 0 && <br />}
              <span>{issue}</span>
            </React.Fragment>
          ))}
        </>
      );
    }
  }

  return (
    <>
      <IntegrationCard
        icon={greenhouseIconLg.src}
        configurationIssue={configurationIssue}
        label="Greenhouse"
        connected={connected}
        onDisconnect={onDisconnect}
        disconnectSaving={disconnectSaving}
        onConnect={onConnect}
      >
        {connected ? (
          <>
            <Typography>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                underline="always"
                className={classes.linkButton}
                component="span"
                onClick={() =>
                  setOpenGreenhouseModal(GreenhouseModalStates.API)
                }
              >
                Update
              </Link>
              &nbsp;Harvest API Key and general settings
            </Typography>
            <Typography>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                underline="always"
                className={classes.linkButton}
                component="span"
                onClick={() =>
                  setOpenGreenhouseModal(GreenhouseModalStates.WEBHOOKS)
                }
              >
                Configure
              </Link>
              &nbsp;Webhooks for realtime updates
            </Typography>
          </>
        ) : (
          <>
            <Typography>
              To get started with Guide, you&apos;ll need to connect your
              Greenhouse account.
            </Typography>
            <br />
            <Typography>
              For help enabling the integration, send us a chat below.
            </Typography>
          </>
        )}
      </IntegrationCard>
      <GreenhouseApiModal
        open={openGreenhouseModal === GreenhouseModalStates.API}
        onClose={() => setOpenGreenhouseModal(GreenhouseModalStates.NONE)}
      />
      <GreenhouseWebhooksModal
        open={openGreenhouseModal === GreenhouseModalStates.WEBHOOKS}
        onClose={() => setOpenGreenhouseModal(GreenhouseModalStates.NONE)}
      />
    </>
  );
}

export default GreenhouseIntegrationCard;
